import * as React from 'react';

/**
 * Canonical pill badge, extracted from the ~60 hand-written copies it replaces.
 * The rendered class set for the default size/tone is identical to the
 * style-book canon: `inline-flex items-center px-2.5 py-0.5 rounded-full
 * text-xs font-medium bg-{tone}-100 text-{tone}-800`.
 *
 * `GenderBadge` deliberately does NOT build on this — it uses a soft outlined
 * style (`bg-{c}-50` + border + icon) that is its own visual language.
 */

export type BadgeTone =
  | 'gray'
  | 'red'
  | 'yellow'
  | 'green'
  | 'blue'
  | 'indigo'
  | 'purple'
  | 'pink'
  | 'amber'
  | 'emerald'
  | 'sky'
  | 'orange'
  | 'slate';

export type BadgeSize = 'xs' | 'sm' | 'md' | 'lg';

const TONE_CLASSES: Record<BadgeTone, string> = {
  gray: 'bg-gray-100 text-gray-800',
  red: 'bg-red-100 text-red-800',
  yellow: 'bg-yellow-100 text-yellow-800',
  green: 'bg-green-100 text-green-800',
  blue: 'bg-blue-100 text-blue-800',
  indigo: 'bg-indigo-100 text-indigo-800',
  purple: 'bg-purple-100 text-purple-800',
  pink: 'bg-pink-100 text-pink-800',
  amber: 'bg-amber-100 text-amber-800',
  emerald: 'bg-emerald-100 text-emerald-800',
  sky: 'bg-sky-100 text-sky-800',
  orange: 'bg-orange-100 text-orange-800',
  slate: 'bg-slate-100 text-slate-800',
};

const SIZE_CLASSES: Record<BadgeSize, string> = {
  xs: 'px-1.5 py-0.5 text-[10px]',
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-0.5 text-xs',
  lg: 'px-3 py-1 text-sm',
};

export interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  size?: BadgeSize;
}

export function Badge({
  tone = 'gray',
  size = 'md',
  className,
  children,
  ...props
}: BadgeProps) {
  const classes = [
    'inline-flex items-center rounded-full font-medium',
    SIZE_CLASSES[size],
    TONE_CLASSES[tone],
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span className={classes} {...props}>
      {children}
    </span>
  );
}

export default Badge;
